/**
 * DisclosuresConfig — manage consent disclosure versions.
 * Each policy_key has at most one current effective row at a time. "New version"
 * auto-retires the prior current row.
 */

import { useEffect, useState } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { createDisclosure, getDisclosures } from '../api';
import type { ConsentDisclosure } from '../types';

const POLICY_KEYS = [
  'terms_of_service',
  'privacy_policy',
  'tcpa_compliance',
  'compliance_guide',
  'acceptable_use',
  'refund_policy',
  'sms_service_consent_text',
  'sms_marketing_consent_text',
  'tc_agreement_text',
];

export default function DisclosuresConfig() {
  const [rows, setRows] = useState<ConsentDisclosure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeRetired, setIncludeRetired] = useState(false);
  const [creating, setCreating] = useState<string | null>(null); // policy_key being created
  const [toast, setToast] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const res = await getDisclosures(includeRetired);
    if (!res.success || !res.data) {
      setError(res.error || 'Failed to load disclosures.');
      setRows([]);
    } else {
      setRows(res.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [includeRetired]);

  const showToast = (text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(null), 3000);
  };

  // Group by policy_key, with current first then by version desc
  const byKey = new Map<string, ConsentDisclosure[]>();
  for (const r of rows) {
    if (!byKey.has(r.policy_key)) byKey.set(r.policy_key, []);
    byKey.get(r.policy_key)!.push(r);
  }
  for (const arr of byKey.values()) {
    arr.sort((a, b) => b.version - a.version);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Consent disclosures</h2>
          <p className="text-sm text-gray-600 mt-0.5">
            Each policy can be revised by creating a new version. Old versions stay in the
            database so historical signups remain interpretable.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={includeRetired}
              onChange={(e) => setIncludeRetired(e.target.checked)}
              className="rounded border-gray-300"
            />
            Show retired
          </label>
          <button
            onClick={load}
            className="p-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {toast && (
        <div className="mb-3 px-3 py-1.5 bg-green-50 border border-green-200 text-green-800 text-sm rounded">
          {toast}
        </div>
      )}

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
          {error}
        </div>
      ) : loading ? (
        <div className="text-center py-8 text-gray-500">
          <RefreshCw className="w-4 h-4 inline-block animate-spin mr-2" />
          Loading…
        </div>
      ) : (
        <div className="space-y-4">
          {POLICY_KEYS.map((key) => {
            const versions = byKey.get(key) || [];
            const current = versions.find((v) => !v.retired_at);
            return (
              <div
                key={key}
                className="bg-white border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 font-mono">
                      {key}
                    </h3>
                    {current ? (
                      <p className="text-xs text-gray-500 mt-0.5">
                        Current version {current.version} · effective since{' '}
                        {new Date(current.effective_at).toLocaleDateString()}
                      </p>
                    ) : (
                      <p className="text-xs text-red-600 mt-0.5">
                        No active version — public form will reject submissions for this
                        policy.
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setCreating(key)}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    <Plus className="w-3 h-3" />
                    New version
                  </button>
                </div>
                {current && (
                  <div className="text-sm text-gray-800 bg-gray-50 rounded p-2 mb-2 whitespace-pre-wrap">
                    {current.text_content}
                  </div>
                )}
                {current?.url && (
                  <p className="text-xs text-blue-600 mb-2">
                    <a
                      href={current.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      {current.url}
                    </a>
                  </p>
                )}
                {includeRetired && versions.filter((v) => v.retired_at).length > 0 && (
                  <details className="mt-2">
                    <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                      {versions.filter((v) => v.retired_at).length} retired version(s)
                    </summary>
                    <ul className="mt-2 space-y-2">
                      {versions
                        .filter((v) => v.retired_at)
                        .map((v) => (
                          <li
                            key={v.id}
                            className="text-xs bg-gray-50 rounded p-2 text-gray-600"
                          >
                            <div className="flex justify-between mb-1">
                              <span>
                                v{v.version} · effective{' '}
                                {new Date(v.effective_at).toLocaleDateString()} ·
                                retired {new Date(v.retired_at!).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="whitespace-pre-wrap">{v.text_content}</div>
                          </li>
                        ))}
                    </ul>
                  </details>
                )}
              </div>
            );
          })}
          {Array.from(byKey.keys())
            .filter((k) => !POLICY_KEYS.includes(k))
            .map((key) => {
              const versions = byKey.get(key) || [];
              const current = versions.find((v) => !v.retired_at);
              return (
                <div
                  key={key}
                  className="bg-yellow-50 border border-yellow-200 rounded-lg p-4"
                >
                  <p className="text-xs text-yellow-800">
                    Unknown policy_key{' '}
                    <span className="font-mono">{key}</span>{' '}
                    {current ? `(v${current.version} current)` : '(no current version)'}
                  </p>
                </div>
              );
            })}
        </div>
      )}

      {creating && (
        <DisclosureEditor
          policyKey={creating}
          onCancel={() => setCreating(null)}
          onSaved={() => {
            setCreating(null);
            load();
            showToast('New version published.');
          }}
        />
      )}
    </div>
  );
}

function DisclosureEditor({
  policyKey,
  onCancel,
  onSaved,
}: {
  policyKey: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [textContent, setTextContent] = useState('');
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!textContent.trim()) {
      setErr('Text content is required.');
      return;
    }
    setSaving(true);
    setErr(null);
    const r = await createDisclosure({
      policy_key: policyKey,
      text_content: textContent,
      url: url.trim() || null,
    });
    setSaving(false);
    if (!r.success) {
      setErr(r.error || 'Save failed');
      return;
    }
    onSaved();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-gray-900 mb-1">
          New version
        </h3>
        <p className="text-xs text-gray-500 font-mono mb-3">{policyKey}</p>
        <p className="text-xs text-gray-600 mb-3">
          The current version will be auto-retired when this saves. Past prospect_consents
          snapshots remain pointed at the prior version IDs.
        </p>
        {err && (
          <div className="mb-3 px-3 py-1.5 bg-red-50 border border-red-200 text-red-800 text-sm rounded">
            {err}
          </div>
        )}
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Text content (verbatim as shown to user)
            </label>
            <textarea
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              rows={6}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              URL (where link points — leave blank for inline consent text)
            </label>
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={saving}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
}
